import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import statsService from '../../services/AGTUps-product-services/statsService';

// Paleta de colores inspirada en Apple
const getAppleColors = (theme) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(28, 28, 30, 0.95)' 
    : 'rgba(255, 255, 255, 0.95)',
  textPrimary: theme.palette.mode === 'dark' 
    ? '#FFFFFF' 
    : '#1D1D1F',
  textSecondary: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.7)' 
    : 'rgba(29, 29, 31, 0.7)'
});

// Gradientes sutiles para cada estadística (light/dark)
const appleGradients = {
  publishedProducts: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  activeNegotiations: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  visualizations: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  searches: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  customerSatisfaction: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  },
  marketplaceRanking: {
    light: 'linear-gradient(145deg, #F5F5F7, #FFFFFF)',
    dark: 'linear-gradient(145deg, #1D1D1F, #2C2C2E)'
  }
};

// Contenedor principal con estilo Apple
const StatsContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : getAppleColors(theme).background,
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
  };
});

// Contenedor interno con ancho máximo al estilo Apple
const StatsInnerBox = styled(Box)(() => ({
  maxWidth: '1200px',
  width: '100%'
}));

// Paper principal con estilo minimalista de Apple
const StatsPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  background: 'transparent',
  boxShadow: 'none',
  color: getAppleColors(theme).textPrimary
}));

// Tarjeta individual con estilo Apple
const StatBox = styled(Box)(({ theme, bgKey }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const mode = theme.palette.mode;
  const gradient = appleGradients[bgKey]?.[mode === 'dark' ? 'dark' : 'light'];

  return {
    borderRadius: theme.spacing(2.5),
    padding: theme.spacing(3),
    background: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : gradient,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  };
});

// Componente de Loading
const LoadingOverlay = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
  width: '100%'
});

const Stats = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await statsService.getAllStats();
        if (response.success) {
          const formattedStats = statsService.formatStats(response.data);
          setStats(formattedStats);
        } else {
          throw new Error(response.error || 'Error fetching stats');
        }
      } catch (err) {
        console.error('Error loading stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();

    // Suscribirse a actualizaciones en tiempo real
    const unsubscribe = statsService.subscribeToUpdates((newStats) => {
      if (newStats.success) {
        const formattedStats = statsService.formatStats(newStats.data);
        setStats(formattedStats);
      }
    });

    // Cleanup
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <LoadingOverlay>
        <CircularProgress />
      </LoadingOverlay>
    );
  }

  if (error) {
    return (
      <StatsContainer>
        <StatsInnerBox>
          <Typography color="error" align="center">
            Error loading statistics: {error}
          </Typography>
        </StatsInnerBox>
      </StatsContainer>
    );
  }

  return (
    <StatsContainer>
      <StatsInnerBox>
        <StatsPaper>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              fontWeight: 600,
              fontSize: '2.5rem',
              letterSpacing: '-0.5px'
            }}
          >
            Statistics
          </Typography>
          
          <Typography
            variant="body1"
            sx={{
              mb: 6,
              color: getAppleColors(theme).textSecondary,
              fontSize: '1.1rem',
              fontWeight: 400
            }}
          >
            Overview of your business metrics
          </Typography>

          <Grid container spacing={3}>
            {stats && Object.entries(stats).map(([key, stat], idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <StatBox bgKey={key}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                      color: getAppleColors(theme).textSecondary,
                      mb: 0.5
                    }}
                  >
                    {stat.label}
                  </Typography>
                  
                  <Typography
                    variant="caption"
                    sx={{
                      color: getAppleColors(theme).textSecondary,
                      opacity: 0.8
                    }}
                  >
                    {stat.description}
                  </Typography>

                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'baseline' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 500,
                        letterSpacing: '-1px'
                      }}
                    >
                      {typeof stat.value === 'number' ? 
                        statsService.formatNumber(stat.value) : 
                        stat.value}
                    </Typography>
                    {stat.unit && (
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 1,
                          fontWeight: 300,
                          color: getAppleColors(theme).textSecondary
                        }}
                      >
                        {stat.unit}
                      </Typography>
                    )}
                  </Box>
                </StatBox>
              </Grid>
            ))}
          </Grid>
        </StatsPaper>
      </StatsInnerBox>
    </StatsContainer>
  );
};

export default Stats;